import { Dialog } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { ISmallSite } from '~/src/dash/api/channels/channels_models';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  IListViewTableColumnInfo,
  RowsPerPage,
  smallTableRowsPerPageOptions,
} from '~/src/dash/components/tables/ListViewTable/common';
import { Col, Cols, Row, Rows, spacing, Text, TextInput } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState } from '~/src/dash/utils/loading-states';
import { isDemoAccount } from '~/src/dash/utils/ui';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  forecastingDataConfig,
  formatSitesModalDataToLVTRows,
} from '~/src/dash/views/tools/Forecasting/Forecast/config';

interface ISiteModalProps {
  openSiteModal: boolean;
  handleCloseSiteModal: () => void;
  sites: ISmallSite[];
  loadState: LoadingState;
}

export const SiteModal = ({
  openSiteModal,
  handleCloseSiteModal,
  sites,
  loadState,
}: ISiteModalProps) => {
  const { isSuperUser, user } = useBaseStore();
  const { cypressClassName, siteModalTable } = forecastingDataConfig;

  const [sitesList, setSitesList] = useState(sites);
  const [searchInput, setSearchInput] = useState('');

  const [currPage, setCurrPage] = useState(siteModalTable.defaultState.page);
  const [rowsPerPage, setRowsPerPage] = useState(siteModalTable.defaultState.rowsPerPage);

  const [orderDirection, setOrderDirection] = useState(siteModalTable.defaultState.orderDirection);
  const [orderBy, setOrderBy] = useState(siteModalTable.defaultState.orderBy);

  const handleSearchInput = (text: string) => {
    const newSitesList = sites.filter((data) => {
      return data.Name.toLowerCase().includes(text.toLowerCase()) || String(data.ID).includes(text);
    });
    unstable_batchedUpdates(() => {
      setSearchInput(text);
      setSitesList(newSitesList);
    });
  };

  // update page number
  const handleChangePage = (_event: React.MouseEvent | null, page: number) => {
    setCurrPage(page);
  };

  // update rows per page
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(updatedRowsPerPage);
      setCurrPage(0);
    });
  };

  // this sets up the changes to force a re-sort
  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo): void => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrderDirection(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setCurrPage(0);
      });
    } else {
      const isAsc = orderBy === key && orderDirection === 'asc';
      unstable_batchedUpdates(() => {
        setOrderDirection(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setCurrPage(0);
      });
    }
  };

  useEffect(() => {
    unstable_batchedUpdates(() => {
      setSitesList(sites);
      setSearchInput('');
    });
  }, [sites]);

  return (
    <Dialog fullWidth maxWidth='md' open={openSiteModal} onClose={handleCloseSiteModal}>
      <Rows basePadding gap={spacing.large}>
        <Row noPadding>
          <Cols
            style={{
              justifyContent: 'space-between',
            }}
          >
            <Col>
              <Rows gap={spacing.small}>
                <Row noPadding>
                  <Text extraLarge heavy>
                    Channels
                  </Text>
                </Row>
                <Row noPadding>
                  <Text>Channels associated with tier</Text>
                </Row>
              </Rows>
            </Col>
            <Col>
              <TextInput
                enableClear
                handleOnChange={handleSearchInput}
                msDelay={800}
                width='180px'
                placeholder='Search by name or ID'
                prefix='search'
                initialValue={searchInput}
                inputStyle={{
                  background: publicaColors.lighterGray,
                }}
              />
            </Col>
          </Cols>
        </Row>
        <Row noPadding>
          <ListViewTable
            activeColumnKeys={[]}
            columns={siteModalTable.columns}
            displayNameWithID={!isDemoAccount(user.user.Username)}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleOnClickColumnHeader={handleOnClickColumnHeader}
            isReadAuthorized
            isSuperUser={isSuperUser}
            isWriteAuthorized
            loadState={loadState}
            order={orderDirection}
            orderBy={orderBy}
            page={currPage}
            rows={formatSitesModalDataToLVTRows(
              sitesList,
              forecastingDataConfig.siteModalTable,
              currPage,
              rowsPerPage,
              orderDirection,
              orderBy,
            )}
            rowsPerPage={rowsPerPage}
            totalRowCount={sitesList.length}
            rowsPerPageOptions={smallTableRowsPerPageOptions}
            className={cypressClassName}
          />
        </Row>
      </Rows>
    </Dialog>
  );
};
